import Phaser from 'phaser'
import { GAME_WIDTH, GAME_HEIGHT } from '../../config'
import { formatScore } from '../../utils/scoring'
import type { MiniGameResult } from './BaseMiniGameScene'

/**
 * Scene shown after completing a mini-game
 * Displays score, star rating, and navigation options
 */
export class MiniGameCompleteScene extends Phaser.Scene {
  private result!: MiniGameResult

  constructor() {
    super({ key: 'MiniGameCompleteScene' })
  }

  init(data: MiniGameResult): void {
    this.result = data
  }

  create(): void {
    this.cameras.main.fadeIn(300)

    this.createBackground()
    this.createResultDisplay()
    this.createButtons()
  }

  private createBackground(): void {
    // Semi-transparent overlay background
    this.add.rectangle(
      GAME_WIDTH / 2,
      GAME_HEIGHT / 2,
      GAME_WIDTH,
      GAME_HEIGHT,
      0x1a1a2e,
      0.95
    )
  }

  private createResultDisplay(): void {
    const centerX = GAME_WIDTH / 2
    const { stars, label, score, maxScore } = this.result

    // Result label (Perfect!, Great!, etc.)
    const labelText = this.add.text(centerX, 100, label, {
      fontFamily: 'system-ui',
      fontSize: '48px',
      fontStyle: 'bold',
      color: this.getLabelColor(stars),
      stroke: '#ffffff',
      strokeThickness: 4,
    })
    labelText.setOrigin(0.5)

    // Animate label entrance
    labelText.setScale(0)
    this.tweens.add({
      targets: labelText,
      scale: 1,
      duration: 400,
      ease: 'Back.easeOut',
    })

    // Stars display
    this.createStars(centerX, 180, stars)

    // Score display
    const scoreText = this.add.text(centerX, 280, `Score: ${formatScore(score)}`, {
      fontFamily: 'system-ui',
      fontSize: '32px',
      fontStyle: 'bold',
      color: '#ffffff',
    })
    scoreText.setOrigin(0.5)
    scoreText.setAlpha(0)

    this.tweens.add({
      targets: scoreText,
      alpha: 1,
      delay: 400,
      duration: 300,
    })

    // Max score reference
    const maxText = this.add.text(centerX, 320, `Max: ${formatScore(maxScore)}`, {
      fontFamily: 'system-ui',
      fontSize: '18px',
      color: '#888888',
    })
    maxText.setOrigin(0.5)
    maxText.setAlpha(0)

    this.tweens.add({
      targets: maxText,
      alpha: 1,
      delay: 500,
      duration: 300,
    })
  }

  private createStars(x: number, y: number, count: number): void {
    const starSpacing = 60
    const totalWidth = starSpacing * 2
    const startX = x - totalWidth / 2

    for (let i = 0; i < 3; i++) {
      const starX = startX + i * starSpacing
      const isEarned = i < count

      const star = this.add.text(starX, y, '★', {
        fontSize: '48px',
        color: isEarned ? '#ffd700' : '#444444',
      })
      star.setOrigin(0.5)

      // Animate earned stars
      if (isEarned) {
        star.setScale(0)
        this.tweens.add({
          targets: star,
          scale: 1,
          duration: 300,
          delay: 200 + i * 150,
          ease: 'Back.easeOut',
        })
      }
    }
  }

  private createButtons(): void {
    const centerX = GAME_WIDTH / 2
    const buttonY = 420

    // Container for buttons (for entrance animation)
    const buttonContainer = this.add.container(centerX, buttonY)

    // Retry button
    const retryBtn = this.createButton(-100, 0, '🔄 Retry', () => {
      this.scene.start(this.result.sceneKey)
    })

    // Next/Menu button
    const nextLabel = this.result.nextScene ? '▶ Next' : '🏠 Menu'
    const nextAction = () => {
      if (this.result.nextScene) {
        this.scene.start(this.result.nextScene)
      } else {
        this.scene.start('MainMenuScene')
      }
    }
    const nextBtn = this.createButton(100, 0, nextLabel, nextAction)

    buttonContainer.add([retryBtn, nextBtn])

    // Animate buttons entrance
    buttonContainer.setAlpha(0)
    buttonContainer.y = buttonY + 50
    this.tweens.add({
      targets: buttonContainer,
      alpha: 1,
      y: buttonY,
      delay: 600,
      duration: 400,
      ease: 'Power2',
    })
  }

  private createButton(
    x: number,
    y: number,
    text: string,
    callback: () => void
  ): Phaser.GameObjects.Container {
    const container = this.add.container(x, y)

    const bg = this.add.rectangle(0, 0, 160, 50, 0xffffff, 0.9)
    bg.setStrokeStyle(3, 0xe65100)

    const label = this.add.text(0, 0, text, {
      fontFamily: 'system-ui',
      fontSize: '20px',
      fontStyle: 'bold',
      color: '#e65100',
    })
    label.setOrigin(0.5)

    container.add([bg, label])

    bg.setInteractive({ useHandCursor: true })
      .on('pointerover', () => {
        bg.setFillStyle(0xffe0b2, 1)
        this.tweens.add({
          targets: container,
          scale: 1.05,
          duration: 100,
        })
      })
      .on('pointerout', () => {
        bg.setFillStyle(0xffffff, 0.9)
        this.tweens.add({
          targets: container,
          scale: 1,
          duration: 100,
        })
      })
      .on('pointerdown', () => {
        this.tweens.add({
          targets: container,
          scale: 0.95,
          duration: 50,
        })
      })
      .on('pointerup', () => {
        this.cameras.main.fadeOut(300)
        this.cameras.main.once('camerafadeoutcomplete', callback)
      })

    return container
  }

  private getLabelColor(stars: number): string {
    switch (stars) {
      case 3:
        return '#ffd700' // Gold
      case 2:
        return '#4caf50' // Green
      case 1:
        return '#ff9800' // Orange
      default:
        return '#888888' // Gray
    }
  }
}
